// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Why a draft carries no jurisdiction entries — Sprint 64.56.0, AC3/AC4.
 *
 * Two states, deliberately not one, and neither is the same claim as "assessed
 * and unknown":
 *
 * - `no_regime_installed` — nothing the organisation installed declares the
 *   capability, so there was no regime to ask about. A setup state an operator
 *   can act on by installing a pack.
 * - `regime_declared_but_inactive` — a framework that declares the capability
 *   IS installed, and every one of them is in a status whose duties are no
 *   longer owed (superseded, or not yet promulgated). Nothing to install;
 *   something to wait for, or an instrument that has been replaced.
 * - `no_assessment_returned` — regimes *were* enumerated and the model returned
 *   no usable assessment of them. Nothing was assessed; this is emphatically
 *   not a finding that no regime applies.
 *
 * Collapsing either into `{ everything: 'unknown' }` is what the fixed default
 * used to do, and it manufactured an assessment of regimes nobody named.
 *
 * ## Why the middle one exists — gate E12.5 (F3)
 *
 * `FrameworkCapabilityRegistry.resolve` has always returned two absence
 * reasons, and its docblock says why in as many words: *"the remedies
 * differ"*. The TIA service read that value, logged it, and returned only the
 * framework list — so both reasons arrived here as an empty array and both
 * were recorded as `no_regime_installed`. A tenant who already had the pack
 * installed was told, in the banner, to install one.
 *
 * The distinction is the registry's, not this module's. Adding the third
 * value is what lets it survive the trip rather than being flattened one call
 * above the surface that needed it.
 */
export const JURISDICTION_ASSESSMENT_ABSENCE_REASONS = [
    'no_regime_installed',
    'regime_declared_but_inactive',
    'no_assessment_returned',
];
/**
 * Read the jurisdiction assessment off a guardrail block — Sprint 64.56.0, AC5.
 *
 * The single reader for both shapes. A record written before 64.56.0 carries
 * `jurisdiction_triggers` (a bare map under `gdpr` / `vn_pdpl`); one written
 * after carries `jurisdiction_assessment`. The legacy map is surfaced with its
 * recorded keys **verbatim** — mapping `gdpr` to a display name would require
 * naming an instrument in platform source, which is the coupling this sprint
 * removed (R-46). Its keys are what was recorded, so they are what is shown.
 *
 * Returns `undefined` when the workflow recorded no assessment at all (a
 * domestic draft), which is a third state again: not asked.
 */
export function readJurisdictionAssessment(guardrails) {
    if (!guardrails)
        return undefined;
    if (guardrails.jurisdiction_assessment)
        return guardrails.jurisdiction_assessment;
    const legacy = guardrails.jurisdiction_triggers;
    if (!legacy || typeof legacy !== 'object')
        return undefined;
    const triggers = { ...legacy };
    return {
        triggers,
        // A legacy record always asserted its two keys, so it always has entries.
        // Guarded anyway: an empty legacy map is "nothing was assessed", not "two
        // regimes were assessed unknown".
        absence_reason: Object.keys(triggers).length > 0 ? null : 'no_assessment_returned',
    };
}
/**
 * A cited regime that the recorded assessment does not cover — Sprint 64.56.0
 * gate E12.5 (F1).
 *
 * Not a fourth trigger state: no assessment ever *records* this value. It is
 * the outcome of a **join** — a citation naming a framework code that appears
 * nowhere in `DraftJurisdictionAssessment.triggers`. It exists so that outcome
 * is a value the selection rule can see, instead of a gap the caller silently
 * drops.
 */
export const CITED_REGIME_NOT_ASSESSED = 'not_assessed';
/**
 * Join a field's citations to the recorded jurisdiction assessment — Sprint
 * 64.56.0 gate E12.5 (F1).
 *
 * ## Why this is a shared function and not three lines at each call site
 *
 * It was three lines at the call site, and the three lines were wrong. The
 * frontend built the trigger list with
 * `codes.map((c) => triggers[c]).filter((s) => s !== undefined)` — **dropping
 * the misses** — and handed the survivors to `isDraftFieldOptIn`, whose rule is
 * `.some((s) => s !== 'applicable')`. An array with every element dropped is
 * empty, `[].some(…)` is `false`, and `false` means *pre-selected*. So the one
 * input that means "nothing here grounds this conclusion" produced the same
 * verdict as "every regime it cites applies".
 *
 * Two live populations hit it:
 *
 *  - a draft written before 64.56.0, whose legacy map is keyed `gdpr` /
 *    `vn_pdpl` while its citations carry the instrument codes `extractCitations`
 *    actually writes (`EU_GDPR`, `VN_PDPL_DECREE_356_2025`) — every lookup
 *    misses, and `absence_reason` is `null` because the map is not empty, so
 *    neither guard fires; and
 *  - a multi-pack tenant where one installed pack declares the capability and
 *    another does not, so a field citing only the non-declaring regime joins to
 *    nothing. That path needs no legacy data at all.
 *
 * ## The rule
 *
 * A cited regime absent from the assessed set is **not** `applicable`. That is
 * strictly at-least-as-safe as the `/gdpr/i.test(code)` rule 64.56.0 deleted —
 * which de-selected precisely because the code is `EU_GDPR` and not `gdpr` —
 * and it is decided by the join rather than by a regime the platform names.
 *
 * It is deliberately more conservative than the rule shipped in 64.56.0, which
 * let a citation outside the assessment pass unexamined on the grounds that "an
 * assessment of personal-data regimes says nothing about a security standard
 * the same field cites". True, but the cost of being wrong is asymmetric: the
 * conservative direction costs a reviewer one deliberate tick, and the
 * permissive direction pre-ticks a legal conclusion with nothing behind it.
 *
 * ## The three states, kept distinct
 *
 *  - `assessment === undefined` — the workflow never asked (a domestic draft, or
 *    one written before guardrails existed). The jurisdiction dimension does not
 *    participate, so this returns `[]` and selection turns on missing facts
 *    alone. Collapsing this into "nothing was assessed" would de-select every
 *    legal conclusion in every pre-guardrail draft.
 *  - `absence_reason` set — regimes were asked about and nothing usable came
 *    back. `[]` here too, because the caller passes `noRegimeAssessed: true` and
 *    that guard already returns opt-in.
 *  - `triggers` populated — the real join, one entry per citation.
 */
export function resolveCitedRegimeTriggers(citedFrameworkCodes, assessment) {
    if (!assessment || assessment.absence_reason)
        return [];
    return citedFrameworkCodes.map((code) => assessment.triggers[code] ?? CITED_REGIME_NOT_ASSESSED);
}
/**
 * Whether a drafted field is **opt-in** (not pre-selected) — Sprint 48.3.6,
 * generalised in 64.56.0 and moved here so the server and the review UI share
 * one copy of the rule rather than two that drift.
 *
 * A field carrying a legal conclusion is opt-in when a required fact is
 * missing, when no regime was assessed at all (nothing could ground the
 * conclusion), or when any regime it cites was not affirmatively triggered.
 * Factual fields with no missing facts stay pre-selected — the 90th-percentile
 * "accept all" flow.
 *
 * The GDPR-specific arguments this replaced (`gdprApplied`, `gdprTrigger`) were
 * the client half of the two-key shape: one named regime decided selection for
 * every tenant, whatever they had installed.
 */
export function isDraftFieldOptIn(args) {
    if (!args.isLegalConclusion)
        return false;
    if (args.hasMissingFacts)
        return true;
    if (args.noRegimeAssessed)
        return true;
    return args.citedRegimeTriggers.some((state) => state !== 'applicable');
}
//# sourceMappingURL=forseti-draft-guardrails.js.map